import { useCallback, useMemo } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CommandsResponse,
  Pick,
  SessionCommand,
  SessionView,
  SlotState,
  StartSessionResponse,
} from '@fitting-room/contracts'
import {
  ApiRequestError,
  GARMENT_PAGE_MAX,
  fetchGarmentsByIds,
  fetchRankedGarments,
  fetchSession,
  postCommands,
  startRefill,
} from '../lib/api'
import { clearAcceptedDrafts } from '../lib/drafts'

export const sessionKey = (token: string) => ['session', token] as const
export const garmentsKey = (token: string, signature: string) => ['garments', token, signature] as const

export function useSessionView(token: string) {
  return useQuery({
    queryKey: sessionKey(token),
    queryFn: ({ signal }) => fetchSession(token, signal),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) return false
      return failureCount < 2
    },
  })
}

/**
 * Anything the server ranks on. Changing an answer must invalidate the deck,
 * and nothing else should.
 *
 * `lineKey` is part of it because the two catalogues are two different decks
 * from the same URL: without it the query key does not move when she switches,
 * TanStack serves the cached made-to-order deck, and the switcher lights up
 * next to gowns from the catalogue she just left. The request that would have
 * corrected it is never made.
 */
export function rankingSignature(view: SessionView): string {
  const { choices, preferenceOrders, ranges, filters } = view.session.answers
  return JSON.stringify([choices, preferenceOrders, ranges, filters, view.session.lineKey ?? null])
}

/** Deep enough that a swipe deck does not run out mid-session. */
const DECK_PAGE = 120

export function useRankedGarments(token: string, view: SessionView, enabled = true) {
  const signature = rankingSignature(view)
  return useQuery({
    queryKey: garmentsKey(token, signature),
    queryFn: ({ signal }) => fetchRankedGarments(token, { limit: DECK_PAGE }, signal),
    enabled,
    staleTime: 60_000,
    // Keep the previous deck on screen while a filter change refetches, so the
    // room does not blink to empty between two valid states.
    placeholderData: keepPreviousData,
  })
}

/**
 * The deepest page the API allows, under a key that does not carry the
 * ranking signature — one request per session however many screens want it.
 *
 * Every other caller of the garments endpoint is asking "what should she see
 * next", which moves with every answer. This one is asking "what does the
 * catalogue hold", which does not, and a key that moved with the answers would
 * mean a fresh request each time a screen wanted to look something up.
 */
export const garmentPageKey = (token: string) => ['garment-page', token, GARMENT_PAGE_MAX] as const

export function useGarmentPage(token: string, enabled = true) {
  return useQuery({
    queryKey: garmentPageKey(token),
    queryFn: ({ signal }) => fetchRankedGarments(token, { limit: GARMENT_PAGE_MAX }, signal),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * Garments the client needs by id rather than by rank — the shortlist and the
 * summary, where what is drawn is her PICKS.
 *
 * This used to read the deep ranked page and match on it, which quietly could
 * not work: a pick can rank below the page, and once a flow has two lines a
 * pick belongs to the catalogue she is not browsing whenever she has switched.
 * Both misses fell through to rendering the raw internal id at her.
 *
 * The ids DO belong in the key now, because they are the request. Sorted, so
 * that two screens wanting the same gowns in a different order share one entry
 * instead of fetching twice.
 */
export const garmentsByIdsKey = (token: string, ids: readonly string[]) =>
  ['garments-by-ids', token, [...ids].sort().join(',')] as const

export function useGarmentsByIds(token: string, ids: readonly string[], enabled = true) {
  return useQuery({
    queryKey: garmentsByIdsKey(token, ids),
    queryFn: ({ signal }) => fetchGarmentsByIds(token, ids, signal),
    enabled: enabled && ids.length > 0,
    staleTime: 60_000,
  })
}

/**
 * "Fill it in again", which is a new session and therefore a new link.
 *
 * Nothing here touches the cache for the current token: the session she is
 * looking at has not changed — that is the whole rule — and the caller
 * navigates to the token this returns, which loads under its own key. A second
 * tap resolves to the refill she already started, so a double press is one
 * session and one destination.
 */
export function useRefill(token: string) {
  return useMutation<StartSessionResponse, ApiRequestError, void>({
    mutationFn: () => startRefill(token),
  })
}

/* ------------------------- optimistic pick handling ----------------------- */

function recomputeSlotStates(previous: readonly SlotState[], picks: readonly Pick[]): SlotState[] {
  return previous.map((state) => {
    const inSlot = picks.filter((p) => p.slotKey === state.key).sort((a, b) => a.position - b.position)
    return {
      ...state,
      used: inSlot.length,
      remaining: state.capacity - inSlot.length,
      over: inSlot.length > state.capacity,
      garmentIds: inSlot.map((p) => p.garmentId),
    }
  })
}

function reindex(picks: readonly Pick[]): Pick[] {
  return picks.map((p, i) => ({ ...p, position: i }))
}

/**
 * Only `pick` and `unpick` are simulated locally, and only in the shape the
 * server's own state machine uses: a garment lives in exactly one slot, so
 * picking it elsewhere MOVES it. Everything else waits for the response —
 * guessing at convergence rounds or step transitions on the client is how the
 * two copies drift apart.
 */
function applyOptimistically(view: SessionView, commands: readonly SessionCommand[]): SessionView {
  let picks: Pick[] = [...view.session.picks]
  let touched = false

  for (const command of commands) {
    if (command.type === 'pick') {
      touched = true
      const previous = picks.find((p) => p.garmentId === command.garmentId)
      const others = picks.filter((p) => p.garmentId !== command.garmentId)
      const target = others.filter((p) => p.slotKey === command.slotKey)
      const rest = others.filter((p) => p.slotKey !== command.slotKey)
      const note = command.note ?? previous?.note
      const created: Pick = {
        garmentId: command.garmentId,
        slotKey: command.slotKey,
        position: target.length,
        ...(note ? { note } : {}),
        addedBy: command.addedBy,
        round: view.session.round,
        createdAt: previous?.createdAt ?? new Date().toISOString(),
      }
      picks = [...rest, ...reindex([...target, created])]
    } else if (command.type === 'unpick') {
      const previous = picks.find((p) => p.garmentId === command.garmentId)
      if (!previous) continue
      touched = true
      const remaining = picks.filter((p) => p.garmentId !== command.garmentId)
      picks = [
        ...remaining.filter((p) => p.slotKey !== previous.slotKey),
        ...reindex(remaining.filter((p) => p.slotKey === previous.slotKey)),
      ]
    }
  }

  if (!touched) return view
  return {
    ...view,
    session: { ...view.session, picks },
    slotStates: recomputeSlotStates(view.slotStates, picks),
  }
}

const RERANKING_COMMANDS = new Set(['answer', 'reorder', 'set_range', 'set_filter'])

/**
 * Commands after which the SESSION VIEW itself has to be re-read, not just
 * patched from the command's response.
 *
 * `onSuccess` merges `session` and `slotStates` back into the cached view,
 * which is everything a command's response carries — and `lineOffer` is not in
 * it. It is a top-level field of the view, computed on the server from her
 * profile, her budget and the line she is on, so a patched cache keeps
 * whatever it had: the offer does not appear when her answers start deserving
 * it, and does not go away when she accepts it. It was only ever right
 * immediately after a full page load, which is exactly how it looked correct
 * in every test that reloaded first.
 *
 * These three are the inputs to that rule. `set_line` is here as well as
 * `range_below`'s `set_range`, because accepting an offer is the moment the
 * offer must stop being made.
 */
export const OFFER_CHANGING_COMMANDS = new Set(['profile', 'set_range', 'set_line'])

export interface SendResult {
  send: (commands: readonly SessionCommand[]) => void
  sendAsync: (commands: readonly SessionCommand[]) => Promise<CommandsResponse>
  pending: boolean
  error: ApiRequestError | null
  reset: () => void
}

/**
 * The mutation scope a link's commands share: TanStack runs mutations of one
 * scope one after another, so a link never has two saves in flight at once.
 */
export function commandsScope(token: string): { id: string } {
  return { id: `commands:${token}` }
}

export function useSendCommands(token: string): SendResult {
  const client = useQueryClient()

  const mutation = useMutation<
    CommandsResponse,
    ApiRequestError,
    readonly SessionCommand[],
    { previous: SessionView | undefined }
  >({
    mutationFn: (commands) => postCommands(token, { commands: [...commands] }),
    // One request at a time per link. The server saves a session only over
    // the version it read, so three hearts tapped in a row sent at once
    // conflict with each other, and the third loses. Scoped, each waits for
    // the one before it; `onMutate` still runs at once, so the heart fills
    // as she taps.
    scope: commandsScope(token),
    onMutate: async (commands) => {
      await client.cancelQueries({ queryKey: sessionKey(token) })
      const previous = client.getQueryData<SessionView>(sessionKey(token))
      if (previous) {
        client.setQueryData<SessionView>(sessionKey(token), applyOptimistically(previous, commands))
      }
      return { previous }
    },
    onError: (_error, _commands, context) => {
      // Roll the deck back to exactly what the server last told us.
      if (context?.previous) client.setQueryData<SessionView>(sessionKey(token), context.previous)
    },
    onSuccess: (response, commands) => {
      client.setQueryData<SessionView>(sessionKey(token), (current) =>
        current ? { ...current, session: response.session, slotStates: response.slotStates } : current,
      )
      // This is the one place that knows a command was accepted rather than
      // merely sent, and it is therefore the only place a draft may be
      // retired: from here on the session above is the truth, and the local
      // copy would only shadow a correction made on the pull sheet.
      clearAcceptedDrafts(token, commands)
    },
    onSettled: (_data, _error, commands) => {
      // The deck only. The deep page is the answer to "what does the catalogue
      // hold", and its two readers — a pick looked up by id, and the picture
      // for a word — both want the same answer all session. Refetching it on
      // every answer is how a screen of photographs comes back different.
      if (commands.some((c) => RERANKING_COMMANDS.has(c.type))) {
        void client.invalidateQueries({ queryKey: ['garments', token] })
      }
      if (commands.some((c) => OFFER_CHANGING_COMMANDS.has(c.type))) {
        void client.invalidateQueries({ queryKey: sessionKey(token) })
      }
    },
  })

  const { mutate, mutateAsync, isPending, error, reset } = mutation

  const send = useCallback((commands: readonly SessionCommand[]) => mutate(commands), [mutate])
  const sendAsync = useCallback((commands: readonly SessionCommand[]) => mutateAsync(commands), [mutateAsync])

  return useMemo(
    () => ({ send, sendAsync, pending: isPending, error: error ?? null, reset }),
    [send, sendAsync, isPending, error, reset],
  )
}
